import React, {useEffect, useMemo} from 'react';
import {AppProps, ConfigProvider} from 'antd';

import getTheme from '@/theme/theme-config';

import {UserProvider} from "@/base/components/auth/UserProvider";
import {NotificationProvider} from "@/base/components/NotificationProvider";
import {Page} from "@/base/types/page";
import RouteLoader from "@/base/components/RouteLoader";
import {antdLocale} from "@/base/utils/antdlocale";
import {useRouter} from "next/router";
import {ClearQueryParams} from "@/base/utils/RouterUtils";
import {PusherProvider} from "@/base/utils/pusher.provider";
import {UserAgentProvider} from "@/base/components/UserAgentProvider";
import {useTheme} from "@/base/components/theme/BaseThemeProvider";
import {ModalProvider} from "@/base/components/ModalProvider";
import {getThemes} from "@/base/utils/theme/theme.util";
import {UserRegionPrefers} from "@/base/types/User/UserRegionPrefers";
import {clarity} from "react-microsoft-clarity";

export type Props = AppProps & {
	Component: Page;
	pageProps: {
		regionConfig?: UserRegionPrefers;
	} & any
}

export const validateTheme = (theme?: string) => {
	const themes = getThemes();

	if (theme && themes.includes(theme))
	{
		return theme;
	}

	return undefined;
}

const App = ({Component, pageProps: {session, regionConfig = undefined, defaultTheme = 'light', _referer = '', _comeFrom = '', ...pageProps}}: Props) => {
	const getLayout = Component.getLayout || ((page) => page)

	const router = useRouter();

	useEffect(() => {
		ClearQueryParams(['refresh']);
	}, [router.query.refresh]);

	if (typeof window !== 'undefined')
	{
		// @ts-ignore
		if (!window?.__SP_DEBUG_CONSOLE)
		{
			// @ts-ignore
			window.__SP_DEBUG_CONSOLE = true;

			if (process.env.NEXT_PUBLIC_NODE_ENV == 'production')
			{
				console.debug = () => false;
			}
			else
			{
				const oldDebug = console.debug;
				console.debug = (...args) => {
					oldDebug("[Debug]", ...args);
				}
			}

			const oldConsoleError = console.error;
			console.error = (...args) => {
				oldConsoleError("[Error]", ...args.map((arg) => arg));
			}

			const oldConsoleWarn = console.warn;
			console.warn = (...args) => {
				oldConsoleWarn("[Warning]", ...args.map((arg) => arg));
			}
		}
	}

	useEffect(() => {
		console.log("Clarify init", process.env.NEXT_PUBLIC_CLARITY_ID)
		if (process.env.NEXT_PUBLIC_CLARITY_ID && process.env.NEXT_PUBLIC_CLARITY_ID.length > 1)
		{
			clarity.init(process.env.NEXT_PUBLIC_CLARITY_ID as string);
		}
	}, []);

	// const [mounted, setMounted] = useState(false)
	// useEffect(() => {
	// 	setMounted(true);
	// }, [])

	const {theme} = useTheme();

	// memo layout  use
	const layout = useMemo(() => {
		return getLayout(<Component {...pageProps} />, pageProps);
	}, [pageProps]);

	return (
		<UserAgentProvider userAgentData={pageProps.userAgentData || {}}>
			<RouteLoader/>
			<UserProvider session={session} comeFrom={_comeFrom} referer={_referer} regionPrefer={regionConfig}>
				<ConfigProvider theme={getTheme(theme || defaultTheme)} locale={antdLocale[router?.locale || 'uk'] ?? antdLocale['uk']}>
					<PusherProvider>
						<NotificationProvider>
							<ModalProvider>
								{layout}
							</ModalProvider>
						</NotificationProvider>
					</PusherProvider>
				</ConfigProvider>
			</UserProvider>
		</UserAgentProvider>
	);
};

export default App;